import React, { useEffect, useState } from "react";
import { useFormik, FieldArray, FormikProvider } from "formik";
import { useUserContext } from "../../context";
import * as Yup from 'yup';
import { useApi } from "../../hooks/useApi";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardBody, CardHeader, Dialog, DialogBody, Typography } from "@material-tailwind/react";
import { ClockLoader } from "react-spinners";

const InvoiceType = Object.freeze({
    ROOM: "ROOM",
    FOOD: "FOOD",
});

export default function EditOTFTaxInvoice() {
    const { invoiceNo } = useParams();
    const { fetchAccessToken, defaultHotel, assignments } = useUserContext();
    const [isLoading, setIsLoading] = useState(false);
    const { getOTFTaxInvoiceByNo, saveOTFTaxInvoice } = useApi();
    const navigate = useNavigate();
    const [roomCategories, setRoomCategories] = useState([]);
    const [taxInvoice, setTaxInvoice] = useState(null);
    const [initialValues, setInitialValues] = useState(null);



    function toDateTimeLocalString(date) {
        const d = new Date(date);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');

        return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
    }

    function toDateLocalString(date) {
        const d = new Date(date);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');

        return `${yyyy}-${mm}-${dd}`;
    }

    function parseDDMMYYYYtoDate(dateStr) {
        const [datePart, timePart] = dateStr.split(" ");
        const [dd, MM, yyyy] = datePart.split("-").map(Number);
        const [hh = 0, mm = 0] = (timePart || "").split(":").map(Number);
        return new Date(yyyy, MM - 1, dd, hh, mm);
    }

    const now = new Date();
    const yesterday = new Date();
    yesterday.setHours(now.getHours() - 23);

    useEffect(() => {
        let getInvoiceInternal = async (hotelId, invoiceNo) => {
            setIsLoading(true);
            try {
                const token = await fetchAccessToken();
                const taxInvoice = await getOTFTaxInvoiceByNo(hotelId, invoiceNo, token);
                if (taxInvoice) {
                    setTaxInvoice(taxInvoice);
                    setInitialValues({
                        invoiceType: taxInvoice.invoiceType || InvoiceType.ROOM,
                        invoiceNo: taxInvoice.invoiceNo,
                        invoiceDate: toDateLocalString(parseDDMMYYYYtoDate(taxInvoice.invoiceDate)),
                        invoiceDateEpoch: taxInvoice.invoiceDateEpoch,
                        gstAmount: taxInvoice.gstAmount,
                        gstPercentage: taxInvoice.gstPercentage || 5,
                        guestName: taxInvoice.guestName,
                        guestMobile: taxInvoice.guestMobile,
                        companyGST: taxInvoice.companyGST,
                        companyDetails: taxInvoice.companyDetails,
                        roomDetails: taxInvoice.roomDetails.map(room => ({
                            roomNo: room.roomNo,
                            category: room.category,
                            description: room.description,
                            hsnCode: room.hsnCode,
                            count: room.count,
                            rate: room.rate
                        })),
                        checkInDateTime: taxInvoice.checkInDateTime ? toDateTimeLocalString(parseDDMMYYYYtoDate(taxInvoice.checkInDateTime)) : toDateTimeLocalString(yesterday),
                        checkOutDateTime: taxInvoice.checkOutDateTime ? toDateTimeLocalString(parseDDMMYYYYtoDate(taxInvoice.checkOutDateTime)) : toDateTimeLocalString(now),
                        totalDays: taxInvoice.totalDays || 1,
                        gst: taxInvoice.gst || true, // Default to true if not set
                    });
                } else {
                    alert("Invoice doesn't exist in the hotel.");
                }
            } catch (ex) {
                alert("Something went wrong.");
            } finally {
                setIsLoading(false);
            }
        }
        if (defaultHotel && invoiceNo) {
            getInvoiceInternal(defaultHotel, invoiceNo);
            let hotel = assignments?.hotels.find(hotel => hotel.id.toLowerCase() === defaultHotel.toLowerCase());
            setRoomCategories(hotel.roomTypes);
        }
    }, [defaultHotel, invoiceNo]);

    function getTodayDateString() {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }

    const formik = useFormik({
        initialValues: initialValues || {
            invoiceType: InvoiceType.ROOM,
            invoiceNo: "",
            invoiceDate: getTodayDateString(),
            invoiceDateEpoch: Date.now(),
            gstAmount: 0,
            gstPercentage: 5,
            guestName: "",
            guestMobile: "",
            companyGST: "",
            companyDetails: "",
            roomDetails: [],
            checkInDateTime: toDateTimeLocalString(yesterday),
            checkOutDateTime: toDateTimeLocalString(now),
            totalDays: 1,
            gst: true,
        },
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            guestMobile: Yup.string().required('Guest Mobile number is required'),
            guestName: Yup.string().required('Guest Name is required'),
            roomDetails: Yup.array().when('invoiceType', {
                is: InvoiceType.ROOM,
                then: () => Yup.array().of(
                    Yup.object().shape({
                        roomNo: Yup.string().required('Room number is required'),
                        category: Yup.string().required('Room category is required')
                    })
                ).min(1, 'Room details cannot be empty'),
                otherwise: () => Yup.array().of(
                    Yup.object().shape({
                        description: Yup.string().required('Description is required'),
                        hsnCode: Yup.string().required('HSN/SAC is required'),
                        count: Yup.number().required('PAX is required').min(1, 'PAX must be greater than 0'),
                        rate: Yup.number().required('Rate is required').min(0, 'Rate must be greater than or equal to 0')
                    })
                ).min(1, 'Particulars cannot be empty')
            }),
            gstAmount: Yup.number().min(0, 'Amount must be greater than or equal to 0'),
            gstPercentage: Yup.number().oneOf([5, 12, 18, 28], 'Invalid GST percentage').required('GST percentage is required'),
            invoiceDate: Yup.date().required('Invoice date is required'),
            checkInDateTime: Yup.date().required('Check-in date is required'),
            checkOutDateTime: Yup.date().required('Checkout date is required'),
            totalDays: Yup.number().min(1, 'Total days must be greater than 0')
        }),
        onSubmit: async (values) => {
            if (!defaultHotel) {
                alert("Please select a hotel first.");
                return;
            }
            try {
                setIsLoading(true);
                const taxInvoiceData = {
                    hotelId: defaultHotel,
                    cardNo: 0,
                    invoiceNo: taxInvoice.invoiceNo,
                    invoiceDate: formatDateToDDMMYYYY(values.invoiceDate),
                    invoiceDateEpoch: values.invoiceDateEpoch,
                    gstAmount: values.gstAmount,
                    gstPercentage: values.gstPercentage,
                    guestName: values.guestName,
                    guestMobile: values.guestMobile,
                    companyGST: values.companyGST,
                    companyDetails: values.companyDetails,
                    roomDetails: [],
                    checkInDateTime: formatToCustomDateTime(values.checkInDateTime),
                    checkOutDateTime: formatToCustomDateTime(values.checkOutDateTime),
                    totalDays: values.invoiceType === InvoiceType.ROOM ? values.totalDays : 1,
                    gst: values.gst,
                    createdOn: taxInvoice.createdOn,
                    update: true,
                    invoiceType: values.invoiceType
                }
                if (values.invoiceType === InvoiceType.ROOM) {
                    taxInvoiceData.roomDetails = values.roomDetails.map(room => ({
                        roomNo: room.roomNo,
                        category: room.category,
                        totalDays: taxInvoiceData.totalDays,
                    }));
                } else {
                    taxInvoiceData.roomDetails = values.roomDetails.map(room => ({
                        description: room.description,
                        hsnCode: room.hsnCode,
                        count: room.count,
                        rate: room.rate
                    }));
                }
                const token = await fetchAccessToken();
                await saveOTFTaxInvoice(defaultHotel, taxInvoiceData, token);
                alert("OTF Tax invoice saved successfully!");
                setIsLoading(false);
                navigate("/admin/otf-tax-invoices");
            } catch (error) {
                setIsLoading(false);
                alert("Failed to save tax invoice. Please try again.");
            }
        },
        validateOnBlur: true,
        validateOnChange: false,
    });

    useEffect(() => {
        const checkInDateTime = formik.values.checkInDateTime;
        const checkOutDateTime = formik.values.checkOutDateTime;
        let gracePeriod = getGracePeriod();
        if (checkInDateTime && checkOutDateTime) {
            const totalDays = getDateDifference(checkInDateTime, checkOutDateTime, gracePeriod);
            formik.setFieldValue("totalDays", totalDays);
        } else {
            formik.setFieldValue("totalDays", 1);
        }
    }, [formik.values.checkInDateTime, formik.values.checkOutDateTime]);

    useEffect(() => {
        if (formik.values.invoiceType === InvoiceType.FOOD) {
            const totalAmount = formik.values.roomDetails.reduce((sum, room) => {
                const count = parseInt(room.count) || 0;
                const rate = parseInt(room.rate) || 0;
                return sum + (count * rate);
            }, 0);

            const gstMultiplier = 1 + (formik.values.gstPercentage / 100);
            const gstAmount = totalAmount * gstMultiplier;

            formik.setFieldValue("gstAmount", gstAmount);
        }
    }, [formik.values.roomDetails, formik.values.gstPercentage, formik.values.invoiceType]);

    function formatToCustomDateTime(dateString) {
        const date = new Date(dateString);

        const dd = String(date.getDate()).padStart(2, '0');
        const MM = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        const hh = String(date.getHours()).padStart(2, '0');
        const mm = String(date.getMinutes()).padStart(2, '0');

        return `${dd}-${MM}-${yyyy} ${hh}:${mm}`;
    }

    function formatDateToDDMMYYYY(dateString) {
        const date = new Date(dateString);
        const dd = String(date.getDate()).padStart(2, '0');
        const MM = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const yyyy = date.getFullYear();
        return `${dd}-${MM}-${yyyy}`;
    }

     function getGracePeriod() {
        let hotel = assignments?.hotels.find(hotel => hotel.id.toLowerCase() === defaultHotel.toLowerCase());
        return hotel.gracePeriod || 0;
    }

    function getDateDifference(dateString1, dateString2, gracePeriod = 0) {
        const date1 = new Date(dateString1);
        const date2 = new Date(dateString2);

        if (isNaN(date1.getTime()) || isNaN(date2.getTime())) {
            throw new Error("Invalid date format. Expected YYYY-MM-DDTHH:mm");
        }
        date2.setHours(date2.getHours() - gracePeriod);
        const differenceMs = Math.abs(date2 - date1);
        const differenceDays = Math.ceil(differenceMs / (1000 * 60 * 60 * 24));

        return differenceDays === 0 ? 1 : differenceDays;
    }

    const handleCustomSubmit = async (e) => {
        e.preventDefault();
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
            const errorMessages = Object.values(errors).flatMap((v) => {
                if (Array.isArray(v)) return v;
                if (typeof v === 'object') return Object.values(v);
                return v;
            });
            alert("Validation Errors:\n" + errorMessages.join("\n"));
        } else {
            formik.handleSubmit();
        }
    };

    return (
        <div>
            <Card className="max-w-2xl mx-auto bg-white shadow rounded">
                <CardHeader variant="gradient" color="gray" className="mt-6 mb-8 p-6 flex flex-row justify-between">
                    <Typography variant="h6" color="white">
                        Edit On-the-fly Invoice
                    </Typography>

                </CardHeader>
                <CardBody className="overflow-scroll px-0 pt-0 pb-2 p-6">
                    <form onSubmit={handleCustomSubmit} className="space-y-4">
                        <div>
                            <label className="block font-medium">Invoice Type <span className="text-red-500">*</span></label>
                            <select label="Category" color="blue" className="bg-white text-gray-900 border border-gray-300 rounded-md py-2 pl-3 pr-10 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                name={`invoiceType`} id={`invoiceType`}
                                onChange={formik.handleChange}
                                value={formik.values.invoiceType} disabled={true}>
                                <option key="ROOM" value="ROOM">Room</option>
                                <option key="FOOD" value="FOOD">Food</option>
                            </select>
                        </div>
                        <div>
                            <label className="block font-medium">Invoice No <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="invoiceNo"
                                value={formik.values.invoiceNo} disabled={true}
                                className="mt-1 block w-full border rounded px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block font-medium">Invoice Date <span className="text-red-500">*</span></label>
                            <input
                                type="date"
                                name="invoiceDate"
                                onChange={e => {
                                    formik.handleChange(e);
                                    const selectedDate = e.target.value;
                                    if (selectedDate) {
                                        const now = new Date();
                                        const hours = now.getHours();
                                        const minutes = now.getMinutes();
                                        const seconds = now.getSeconds();
                                        const milliseconds = now.getMilliseconds();
                                        const fullDate = new Date(selectedDate);
                                        fullDate.setHours(hours, minutes, seconds, milliseconds);
                                        formik.setFieldValue("invoiceDateEpoch", fullDate.getTime());
                                    }
                                }}
                                value={formik.values.invoiceDate}
                                className="mt-1 block w-full border rounded px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block font-medium">Total Amount (incl. GST) <span className="text-red-500">*</span></label>
                            <input
                                type="number"
                                name="gstAmount"
                                onChange={formik.handleChange}
                                value={formik.values.gstAmount}
                                disabled={formik.values.invoiceType === InvoiceType.FOOD}
                                className="mt-1 block w-full border rounded px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block font-medium">GST percentage <span className="text-red-500">*</span></label>
                            <input
                                type="number"
                                name="gstPercentage"
                                onChange={formik.handleChange}
                                value={formik.values.gstPercentage}
                                className="mt-1 block w-full border rounded px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block font-medium">Guest Name <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="guestName"
                                onChange={formik.handleChange}
                                value={formik.values.guestName}
                                className="mt-1 block w-full border rounded px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block font-medium">Guest Mobile <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="guestMobile"
                                onChange={formik.handleChange}
                                value={formik.values.guestMobile}
                                className="mt-1 block w-full border rounded px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block font-medium">Company GST</label>
                            <input
                                type="text"
                                name="companyGST"
                                onChange={formik.handleChange}
                                value={formik.values.companyGST}
                                className="mt-1 block w-full border rounded px-3 py-2"
                            />
                        </div>
                        <div>
                            <label className="block font-medium">Company Details</label>
                            <textarea
                                name="companyDetails"
                                onChange={formik.handleChange}
                                value={formik.values.companyDetails}
                                className="mt-1 block w-full border rounded px-3 py-2"
                            />
                        </div>
                        <FormikProvider value={formik}>
                            <FieldArray
                                name="roomDetails"
                                render={arrayHelpers => (
                                    <div>
                                        <label className="block font-medium mb-2">{formik.values.invoiceType === InvoiceType.ROOM ? 'Room Details' : 'Service Details'} <span className="text-red-500">*</span></label>
                                        {formik.values.roomDetails.map((room, idx) => (
                                            <div key={idx} className="mb-4 border p-3 rounded bg-gray-50 overflow-x-auto">
                                                <div className="flex flex-col sm:flex-row gap-2 mb-2">
                                                    {formik.values.invoiceType === InvoiceType.ROOM &&
                                                        <input
                                                            type="text"
                                                            name={`roomDetails[${idx}].roomNo`}
                                                            placeholder="Room No"
                                                            onChange={formik.handleChange}
                                                            value={room.roomNo}
                                                            className="border rounded px-2 py-1 flex-1"
                                                        />
                                                    }
                                                    {formik.values.invoiceType === InvoiceType.ROOM &&
                                                        <select label="Category" color="blue" className="bg-white text-gray-900 border border-gray-300 rounded-md py-2 pl-3 pr-10 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                                            name={`roomDetails[${idx}].category`} id={`roomDetails[${idx}].category`}
                                                            onChange={formik.handleChange}
                                                            value={room.category}>
                                                            <option value="">Select</option>
                                                            {roomCategories.map((category, i) => (
                                                                <option key={i} value={category.category}>{category.category}</option>
                                                            ))}
                                                        </select>
                                                    }
                                                    {formik.values.invoiceType === InvoiceType.FOOD &&
                                                        <div className="flex-1">
                                                            <label className="block text-sm font-medium mb-1">Description <span className="text-red-500">*</span></label>
                                                            <input
                                                                type="text"
                                                                name={`roomDetails[${idx}].description`}
                                                                placeholder="Description"
                                                                onChange={formik.handleChange}
                                                                value={room.description}
                                                                className="border rounded px-2 py-1 w-full"
                                                            />
                                                        </div>
                                                    }
                                                    {formik.values.invoiceType === InvoiceType.FOOD &&
                                                        <div className="flex-1">
                                                            <label className="block text-sm font-medium mb-1">HSN/SAC <span className="text-red-500">*</span></label>
                                                            <input
                                                                type="text"
                                                                name={`roomDetails[${idx}].hsnCode`}
                                                                placeholder="HSN/SAC"
                                                                onChange={formik.handleChange}
                                                                value={room.hsnCode}
                                                                className="border rounded px-2 py-1 w-full"
                                                            />
                                                        </div>
                                                    }
                                                    {formik.values.invoiceType === InvoiceType.FOOD &&
                                                        <div className="flex-1">
                                                            <label className="block text-sm font-medium mb-1">PAX <span className="text-red-500">*</span></label>
                                                            <input
                                                                type="number"
                                                                name={`roomDetails[${idx}].count`}
                                                                placeholder="PAX"
                                                                onChange={formik.handleChange}
                                                                value={room.count}
                                                                className="border rounded px-2 py-1 w-full"
                                                            />
                                                        </div>
                                                    }
                                                    {formik.values.invoiceType === InvoiceType.FOOD &&
                                                        <div className="flex-1">
                                                            <label className="block text-sm font-medium mb-1">Rate <span className="text-red-500">*</span></label>
                                                            <input
                                                                type="number"
                                                                name={`roomDetails[${idx}].rate`}
                                                                placeholder="Rate"
                                                                onChange={formik.handleChange}
                                                                value={room.rate}
                                                                className="border rounded px-2 py-1 w-full"
                                                            />
                                                        </div>
                                                    }
                                                    <button
                                                        type="button"
                                                        onClick={() => arrayHelpers.remove(idx)}
                                                        className="text-red-500 px-2"
                                                        disabled={formik.values.roomDetails.length === 1}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                arrayHelpers.push({ roomNo: "", category: "", description: "", hsnCode: "", count: 1, rate: 0 })
                                            }
                                            className="bg-blue-500 text-white px-3 py-1 rounded"
                                        >
                                            {formik.values.invoiceType === InvoiceType.FOOD ? 'Add Service' : 'Add Room'}
                                        </button>
                                    </div>
                                )}
                            />
                        </FormikProvider>
                        {formik.values.invoiceType === InvoiceType.ROOM &&
                            <div>
                                <label className="block font-medium">Check-In DateTime <span className="text-red-500">*</span></label>
                                <input
                                    type="datetime-local"
                                    name="checkInDateTime"
                                    onChange={formik.handleChange}
                                    value={formik.values.checkInDateTime}
                                    className="mt-1 block w-full border rounded px-3 py-2"
                                />
                            </div>
                        }
                        {formik.values.invoiceType === InvoiceType.ROOM &&
                            <div>
                                <label className="block font-medium">Check-Out DateTime <span className="text-red-500">*</span></label>
                                <input
                                    type="datetime-local"
                                    name="checkOutDateTime"
                                    onChange={formik.handleChange}
                                    value={formik.values.checkOutDateTime}
                                    className="mt-1 block w-full border rounded px-3 py-2"
                                />
                            </div>
                        }
                        {formik.values.invoiceType === InvoiceType.ROOM &&
                            <div>
                                <label className="block font-medium">Total Days</label>
                                <input
                                    type="number"
                                    name="totalDays"
                                    onChange={formik.handleChange}
                                    value={formik.values.totalDays} disabled={true}
                                    className="mt-1 block w-full border rounded px-3 py-2"
                                />
                            </div>
                        }
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                name="gst"
                                onChange={formik.handleChange}
                                checked={formik.values.gst}
                                className="mr-2" disabled={true}
                            />
                            <label className="font-medium">is GST Bill</label>
                        </div>
                        <button
                            type="submit"
                            className="w-full bg-green-600 text-white py-2 rounded font-semibold"
                        >
                            Save
                        </button>
                    </form>
                </CardBody>
            </Card>
            <Dialog open={isLoading} className="bg-transparent shadow-none" size="md">
                <DialogBody className="bg-transparent shadow-none flex justify-center">
                    <ClockLoader color="#2be60d" />
                </DialogBody>
            </Dialog>
        </div>
    );
}